import React, { useEffect, useRef, useState } from 'react';
import { placesAPI } from '../api/api';
import useEsMovil from '../hooks/useEsMovil';

/**
 * Input de dirección con autocompletado de Google Places (vía backend).
 *
 * Sustituye al antiguo widget google.maps.places.Autocomplete (deprecado por
 * Google para proyectos nuevos). Las sugerencias salen de
 * GET /api/places/autocomplete y, al elegir una, se piden los detalles
 * (dirección formateada + coordenadas) a /api/places/detalles.
 *
 * Props:
 * - value / onChange(texto): input controlado, como un <input> normal.
 * - onSelect({ place_id, nombre, direccion, lat, lng }): al elegir sugerencia.
 * - id, placeholder, className, required, disabled: se pasan al input.
 */
const DireccionAutocomplete = ({
  value,
  onChange,
  onSelect,
  id,
  placeholder = 'Escribe una dirección...',
  className = '',
  required = false,
  disabled = false,
}) => {
  const [sugerencias, setSugerencias] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [activa, setActiva] = useState(-1);
  const debounceRef = useRef(null);
  const contenedorRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const ultimaQueryRef = useRef('');
  const campoRef = useRef(null);
  const subirRef = useRef(null);
  const esMovil = useEsMovil();

  // Móvil: al enfocar, el campo sube arriba del todo para que las sugerencias
  // queden a la vista encima del teclado (en el escritorio no hace nada)
  const subirCampoEnMovil = () => {
    if (!esMovil) return;
    clearTimeout(subirRef.current);
    subirRef.current = setTimeout(() => {
      try {
        campoRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } catch (e) {
        // Navegador sin scrollIntoView con opciones: no pasa nada
      }
    }, 300);
  };

  // Token de sesión de Places: uno por "sesión de tecleo" (se renueva al elegir).
  const getSessionToken = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    }
    return sessionTokenRef.current;
  };

  // Cerrar el dropdown al hacer click fuera
  useEffect(() => {
    const onClickFuera = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, []);

  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    clearTimeout(subirRef.current);
  }, []);

  const buscar = (texto) => {
    clearTimeout(debounceRef.current);
    if (!texto || texto.trim().length < 3) {
      setSugerencias([]);
      setAbierto(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      ultimaQueryRef.current = texto;
      try {
        const { data } = await placesAPI.autocomplete(texto, getSessionToken());
        // Descartar respuestas tardías de queries anteriores
        if (ultimaQueryRef.current !== texto) return;
        setSugerencias(data.sugerencias || []);
        setAbierto((data.sugerencias || []).length > 0);
        setActiva(-1);
      } catch {
        setSugerencias([]);
        setAbierto(false);
      }
    }, 300);
  };

  const handleChange = (e) => {
    // Por si pegan texto con saltos de línea (el campo es de una sola línea)
    const texto = e.target.value.replace(/\s*\n\s*/g, ' ');
    onChange?.(texto);
    buscar(texto);
  };

  const elegir = async (sug) => {
    setAbierto(false);
    setSugerencias([]);
    onChange?.(sug.descripcion);
    const token = getSessionToken();
    sessionTokenRef.current = null; // la sesión de Places termina al elegir
    try {
      const { data } = await placesAPI.detalles(sug.place_id, token);
      onChange?.(data.direccion || sug.descripcion);
      onSelect?.(data);
    } catch {
      // Sin detalles (red, etc.): nos quedamos con el texto de la sugerencia
      onSelect?.({ place_id: sug.place_id, nombre: sug.principal, direccion: sug.descripcion, lat: null, lng: null });
    }
  };

  const onKeyDown = (e) => {
    // El campo es un textarea de una línea: Enter nunca debe meter salto de línea.
    if (e.key === 'Enter') {
      e.preventDefault();
      if (abierto && activa >= 0) elegir(sugerencias[activa]);
      return;
    }
    if (!abierto || sugerencias.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiva((a) => Math.min(a + 1, sugerencias.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiva((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Escape') {
      setAbierto(false);
    }
  };

  return (
    <div ref={contenedorRef} className="relative">
      {/* textarea de 1 línea en vez de input: el autofill de direcciones de
          Chrome (y su desplegable de valores anteriores) solo actúa sobre
          inputs, así que no tapa nuestras sugerencias ni rellena solo. */}
      <textarea
        ref={campoRef}
        rows={1}
        id={id}
        value={value || ''}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (sugerencias.length > 0) setAbierto(true);
          subirCampoEnMovil();
        }}
        placeholder={placeholder}
        className={`${className} resize-none overflow-hidden whitespace-nowrap`}
        required={required}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        wrap="off"
      />
      {abierto && (
        <ul className="absolute z-[99999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
          {sugerencias.map((s, i) => (
            <li key={s.place_id || i}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => elegir(s)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 max-md:min-h-[44px] max-md:py-2.5 ${i === activa ? 'bg-gray-100' : ''}`}
              >
                <span className="font-medium text-gray-900">{s.principal}</span>
                {s.secundario && <span className="text-gray-500"> · {s.secundario}</span>}
              </button>
            </li>
          ))}
          <li className="px-3 py-1 text-[10px] max-md:text-xs text-gray-400 border-t">Resultados de Google</li>
        </ul>
      )}
    </div>
  );
};

export default DireccionAutocomplete;
